import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import Notice from '../../Notice';
import { MessageConfig, MessageOptions } from '../types';

interface INotice extends MessageOptions {
  id: string | number;
}

interface MessageContainerProps {
  config: MessageConfig;
}

/**
 * 向外暴露的方法
 */
export interface MessageContainerRef {
  add: (notice: MessageOptions) => () => void;
  remove: (id: string | number) => void;
  destroy: () => void;
  updateConfig: (config: MessageConfig) => void;
  update: (key: string | number, notice: Partial<MessageOptions>) => void;
}

const MessageContainer = forwardRef<MessageContainerRef, MessageContainerProps>(
  (props, ref) => {
    const { config: initialConfig } = props;
    const [config, setConfig] = useState(initialConfig);
    const [notices, setNotices] = useState<INotice[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const idSeedRef = useRef(0);

    /**
     * 生成唯一ID
     */
    const getUniqueId = useCallback(() => {
      idSeedRef.current += 1;
      return `message_${Date.now()}_${idSeedRef.current}`;
    }, []);

    /**
     * 添加消息
     */
    const add = useCallback(
      (notice: MessageOptions) => {
        const { maxCount = Infinity } = config;
        const key = notice.key || getUniqueId();

        setNotices((prevNotices) => {
          // 检查是否有相同key的消息
          const updatedNotices = prevNotices.filter((item) => item.id !== key);

          // 创建新消息
          const newNotice: INotice = {
            ...notice,
            id: key,
          };

          // 如果超过最大数量，移除最早的消息
          let resultNotices = [...updatedNotices, newNotice];
          if (resultNotices.length > maxCount) {
            resultNotices = resultNotices.slice(
              resultNotices.length - maxCount,
            );
          }

          return resultNotices;
        });

        return () => {
          remove(key);
        };
      },
      [config, getUniqueId],
    );

    /**
     * 移除消息
     */
    const remove = useCallback((id?: string | number) => {
      setNotices((prevNotices) => {
        const notice = prevNotices.find((item) => item.id === id);
        notice?.onClose?.(id);
        return prevNotices.filter((item) => item.id !== id);
      });
    }, []);

    /**
     * 清除所有消息
     */
    const destroy = useCallback(() => {
      setNotices([]);
    }, []);
    const updateConfig = useCallback((newConfig: MessageConfig) => {
      setConfig((prevConfig) => ({ ...prevConfig, ...newConfig }));
    }, []);

    /**
     * 更新内容
     */
    const update = useCallback(
      (key: string | number, newNotice: Partial<MessageOptions>) => {
        setNotices((prevNotices) => {
          return prevNotices.map((notice) => {
            if (notice.id === key) {
              return { ...notice, ...newNotice };
            }
            return notice;
          });
        });
      },
      [],
    );

    useImperativeHandle(
      ref,
      () => ({
        add,
        remove,
        destroy,
        updateConfig,
        update,
      }),
      [add, remove, destroy, updateConfig, update],
    );

    /**
     * 获取容器位置类名
     */
    const containerClassName = useMemo(() => {
      const { position = 'topCenter' } = config;
      const positionMap: Record<string, string> = {
        topLeft: 'message-top-left',
        topCenter: 'message-top-center',
        topRight: 'message-top-right',
        bottomLeft: 'message-bottom-left',
        bottomCenter: 'message-bottom-center',
        bottomRight: 'message-bottom-right',
      };

      return `odc-message ${positionMap[position] || 'message-top-center'
      }`;
    }, [config.position]);

    /**
     * 获取容器样式
     */
    const containerStyle = useMemo(() => {
      const { top } = config;
      if (top !== undefined && (config.position || '').startsWith('top')) {
        return { top: typeof top === 'number' ? `${top}px` : top };
      }
      return {};
    }, [config.top, config.position]);

    return (
      <div
        className={containerClassName}
        style={containerStyle}
        ref={containerRef}
      >
        {notices.map((notice) => (
          <Notice
            key={notice.id}
            id={notice.id}
            type={notice.type}
            content={notice.content}
            desc={notice.desc}
            duration={
              notice.duration !== undefined ? notice.duration : config.duration
            }
            onRemove={remove}
            className={notice.className}
            style={notice.style}
            onClick={notice.onClick}
            closable={notice.closable}
            actionText={notice.actionText}
            onAction={notice.onAction}
          />
        ))}
      </div>
    );
  },
);

MessageContainer.displayName = 'MessageContainer';

export default MessageContainer;
